import React, { useEffect, useState } from 'react';
import { ComponentType, Component } from '@/types';
import { getActionCardConfig, type ActionButtonConfig } from './actionCardRegistry';
import { ActionCardShell } from './ActionCardShell';
import { PinActionCard, type PinContext } from './PinActionCard';
import { GaugeActionCard, type GaugeContext } from './GaugeActionCard';
import {
  createHomeAssistantUnbindUpdate,
  describeHomeAssistantBinding,
  extractHomeAssistantBinding,
  supportsHomeAssistantBinding,
} from '@/services/homeAssistant/binding';
import type { ColorSchemeWriteTarget } from '@/types/color-scheme';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

type GaugeDisplayMode = GaugeContext['displayMode'];
type PinGroup = PinContext['groups'][number];

const isGaugeDisplayMode = (value: string): value is GaugeDisplayMode =>
  value === 'dial' ||
  value === 'ring' ||
  value === 'bar' ||
  value === 'milestone';

const readGaugeDisplayMode = (value: unknown): GaugeDisplayMode =>
  typeof value === 'string' && isGaugeDisplayMode(value)
    ? value
    : 'dial';

const readThresholds = (value: unknown): GaugeContext['thresholds'] => {
  if (!Array.isArray(value)) return [];

  return value
    .filter(isRecord)
    .map((threshold) => ({
      min: readNumber(threshold.min, 0),
      max: readNumber(threshold.max, 0),
      color: readString(threshold.color) || '#10B981',
      label: readString(threshold.label),
    }));
};

const readPin = (value: unknown): PinContext['pin'] | null => {
  if (!isRecord(value)) return null;
  const id = readString(value.id);
  const name = readString(value.name);
  if (!id || !name) return null;

  const navigation = isRecord(value.navigation)
    ? {
        targetPageId: readString(value.navigation.targetPageId) || '',
        targetPageName: readString(value.navigation.targetPageName),
      }
    : undefined;
  const style = isRecord(value.style)
    ? {
        color: readString(value.style.color),
        icon: readString(value.style.icon),
      }
    : undefined;

  return {
    id,
    name,
    groupId: readString(value.groupId),
    navigation: navigation?.targetPageId ? navigation : undefined,
    style,
  };
};

const readPins = (value: unknown): PinContext['pin'][] =>
  Array.isArray(value)
    ? value.map(readPin).filter((pin): pin is PinContext['pin'] => pin !== null)
    : [];

const readGroup = (value: unknown): PinGroup | null => {
  if (!isRecord(value)) return null;
  const id = readString(value.id);
  const name = readString(value.name);
  if (!id || !name) return null;

  const pinIds = Array.isArray(value.pinIds)
    ? value.pinIds.filter((pinId): pinId is string => typeof pinId === 'string')
    : undefined;

  return {
    id,
    name,
    ...(pinIds ? { pinIds } : {}),
  };
};

const readGroups = (value: unknown): PinContext['groups'] => {
  if (!Array.isArray(value)) return [];

  return value.map(readGroup).filter((group): group is PinGroup => group !== null);
};

interface ActionCardRendererProps {
  component: Component;
  pages: Array<{ id: string; name: string }>;
  currentPageId: string | null;
  updateComponent: (id: string, updates: Partial<Component>) => void;
  onAutoSend: (command: string, styleWriteTarget?: ColorSchemeWriteTarget) => void;
  onPreFill: (text: string, styleWriteTarget?: ColorSchemeWriteTarget) => void;
  onInjectMessage: (content: string) => void;
  onDataDiscovery: () => void;
  onRemoveData?: () => void;
  onIconPicker?: () => void;
  onMapTemplates?: () => void;
  onColorPicker?: (commandPrefix: string, propertyLabel: string, styleWriteTarget?: ColorSchemeWriteTarget) => void;
  onDeviceBinding?: () => void;
  onHomeAssistantBinding?: () => void;
  onDuplicateComponent?: () => void;
  onHideComponent?: () => void;
}

const spacingRelevantTypes = new Set<ComponentType>([
  ComponentType.CONTAINER,
  ComponentType.FLEX_CONTAINER,
  ComponentType.CARD,
  ComponentType.DATA_CARD,
  ComponentType.FORM_CONTAINER,
  ComponentType.LIST,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
  ComponentType.CHECKLIST,
  ComponentType.PROJECT_HUB,
  ComponentType.QUESTIONNAIRE,
  ComponentType.VOTING_CARD,
  ComponentType.ICON_BUTTON,
  ComponentType.PUSH_BUTTON,
  ComponentType.TOGGLE_SWITCH,
  ComponentType.RADIAL_DIAL,
  ComponentType.SLIDER,
  ComponentType.STEPPER,
  ComponentType.SEGMENTED_CONTROL,
  ComponentType.THERMOSTAT,
  ComponentType.FILE_STORAGE,
  ComponentType.LEVEL_INDICATOR,
  ComponentType.STATUS_LIGHT,
  ComponentType.INPUT,
]);

const sizeRelevantTypes = new Set<ComponentType>([
  ComponentType.TEXT,
  ComponentType.DATA_TEXT,
  ComponentType.IMAGE,
  ComponentType.BUTTON,
  ComponentType.ICON,
  ComponentType.CARD,
  ComponentType.DATA_CARD,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.AREA_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.ICON_BUTTON,
  ComponentType.PUSH_BUTTON,
  ComponentType.TOGGLE_SWITCH,
  ComponentType.RADIAL_DIAL,
  ComponentType.SLIDER,
  ComponentType.STEPPER,
  ComponentType.SEGMENTED_CONTROL,
  ComponentType.THERMOSTAT,
  ComponentType.FILE_STORAGE,
  ComponentType.LEVEL_INDICATOR,
  ComponentType.STATUS_LIGHT,
  ComponentType.INPUT,
]);

const alignmentRelevantTypes = new Set<ComponentType>([
  ComponentType.TEXT,
  ComponentType.DATA_TEXT,
  ComponentType.IMAGE,
  ComponentType.BUTTON,
  ComponentType.CARD,
  ComponentType.DATA_CARD,
  ComponentType.CONTAINER,
  ComponentType.FLEX_CONTAINER,
  ComponentType.FORM_CONTAINER,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.ICON_BUTTON,
  ComponentType.PUSH_BUTTON,
  ComponentType.TOGGLE_SWITCH,
  ComponentType.RADIAL_DIAL,
  ComponentType.SLIDER,
  ComponentType.STEPPER,
  ComponentType.SEGMENTED_CONTROL,
  ComponentType.THERMOSTAT,
  ComponentType.LEVEL_INDICATOR,
  ComponentType.STATUS_LIGHT,
  ComponentType.INPUT,
]);

const styleRelevantTypes = new Set<ComponentType>([
  ComponentType.CARD,
  ComponentType.DATA_CARD,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.AREA_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
  ComponentType.FORM_CONTAINER,
]);

const homeAssistantAddRelevantTypes = new Set<ComponentType>([
  ComponentType.CONTAINER,
  ComponentType.FLEX_CONTAINER,
  ComponentType.CARD,
  ComponentType.DATA_CARD,
  ComponentType.FORM_CONTAINER,
]);

const hasButtonLabel = (buttons: ActionButtonConfig[], label: string): boolean =>
  buttons.some((button) => button.label === label);

const hasButtonAction = (
  buttons: ActionButtonConfig[],
  action: ActionButtonConfig['action'],
): boolean =>
  buttons.some((button) => button.action === action);

const appendUniqueButtons = (
  buttons: ActionButtonConfig[],
  additions: ActionButtonConfig[],
): ActionButtonConfig[] => {
  const seen = new Set(buttons.map((button) => button.label));
  const next = [...buttons];
  additions.forEach((button) => {
    if (seen.has(button.label)) return;
    seen.add(button.label);
    next.push(button);
  });
  return next;
};

const getQuickCustomizationButtons = (
  component: Component,
  baseButtons: ActionButtonConfig[],
): ActionButtonConfig[] => {
  const additions: ActionButtonConfig[] = [];

  if (spacingRelevantTypes.has(component.type) && !hasButtonLabel(baseButtons, 'Spacing')) {
    additions.push({ label: 'Spacing', action: 'drilldown', drilldownId: 'selectedSpacing' });
  }
  if (sizeRelevantTypes.has(component.type) && !hasButtonLabel(baseButtons, 'Size')) {
    additions.push({ label: 'Size', action: 'drilldown', drilldownId: 'selectedSize' });
  }
  if (alignmentRelevantTypes.has(component.type) && !hasButtonLabel(baseButtons, 'Align')) {
    additions.push({ label: 'Align', action: 'drilldown', drilldownId: 'selectedAlign' });
  }
  if (
    styleRelevantTypes.has(component.type) &&
    !hasButtonLabel(baseButtons, 'Style') &&
    !hasButtonLabel(baseButtons, 'Theme') &&
    !hasButtonLabel(baseButtons, 'Color')
  ) {
    additions.push({
      label: 'Style',
      action: 'autoSend',
      command: 'restyle this selected component for a Home Assistant dashboard',
    });
  }
  if (homeAssistantAddRelevantTypes.has(component.type) && !hasButtonLabel(baseButtons, 'Add HA')) {
    additions.push({
      label: 'Add HA',
      action: 'drilldown',
      drilldownId: 'selectedHomeAssistantAdd',
    });
  }

  return additions;
};

const getSafeComponentButtons = (
  component: Component,
  options: {
    onDuplicateComponent?: () => void;
    onHideComponent?: () => void;
  },
): ActionButtonConfig[] => {
  const buttons: ActionButtonConfig[] = [];

  if (options.onDuplicateComponent) {
    buttons.push({ label: 'Duplicate', action: 'duplicateComponent' });
  }
  if (options.onHideComponent) {
    buttons.push({
      label: component.hidden ? 'Show' : 'Hide',
      action: 'hideComponent',
    });
  }
  buttons.push({
    label: 'Delete',
    action: 'preFill',
    command: '/delete',
  });

  return buttons;
};

const hasStoredDeviceBinding = (component: Component): boolean => {
  const action = isRecord(component.properties.action) ? component.properties.action : null;
  return isRecord(action?.deviceBinding);
};

const getDeviceBindingLabel = (component: Component, hasHomeAssistantBinding: boolean): string =>
  hasHomeAssistantBinding || hasStoredDeviceBinding(component) ? 'Change Device' : 'Device';

const normalizeDeviceBindingButtons = (
  buttons: ActionButtonConfig[],
  component: Component,
  hasHomeAssistantBinding: boolean,
): ActionButtonConfig[] => {
  const label = getDeviceBindingLabel(component, hasHomeAssistantBinding);
  return buttons.map((button) =>
    button.action === 'deviceBinding'
      ? { ...button, label }
      : button,
  );
};

const getHomeAssistantBindingButtons = (
  component: Component,
  baseButtons: ActionButtonConfig[],
  hasHomeAssistantBinding: boolean,
): ActionButtonConfig[] => {
  if (!supportsHomeAssistantBinding(component.type)) return [];

  const buttons: ActionButtonConfig[] = [];
  if (!hasButtonAction(baseButtons, 'deviceBinding')) {
    buttons.push({
      label: getDeviceBindingLabel(component, hasHomeAssistantBinding),
      action: 'deviceBinding',
    });
  }

  if (hasHomeAssistantBinding) {
    buttons.push(
      { label: 'Remove Binding', action: 'homeAssistantUnbind' },
      { label: 'Explain Binding', action: 'homeAssistantExplain' },
    );
  }

  return buttons;
};

const createHomeAssistantExplainMessage = (component: Component): string => {
  const binding = extractHomeAssistantBinding(component.properties);
  const summary = describeHomeAssistantBinding(component.properties);
  if (!binding || !summary) {
    return 'No Home Assistant binding metadata is stored on the selected component.';
  }

  const action = isRecord(component.properties.action) &&
    component.properties.action.type === 'home-assistant-placeholder'
    ? component.properties.action
    : null;
  const actionSummary = action && typeof action.serviceLabel === 'string'
    ? ` Manual-only action metadata points at ${action.serviceLabel}; it is stored for review and inspection only.`
    : '';
  const reviewSummary = action && action.reviewRequired === true
    ? ' Review is required before any real-world execution path is added.'
    : '';

  return `${summary}. Attribute: ${binding.attribute ?? 'state'}. This is metadata only; TerraMatrix does not execute Home Assistant services from this MVP binding.${actionSummary}${reviewSummary}`;
};

const stylePropertyFromCommand = (commandOrPrefix: string): string | undefined => {
  const normalized = commandOrPrefix.toLowerCase();
  if (normalized.includes('background')) return 'backgroundColor';
  if (normalized.includes('border')) return 'borderColor';
  if (normalized.includes('color')) return 'color';
  if (normalized.includes('padding')) return 'padding';
  if (normalized.includes('gap')) return 'gap';
  if (normalized.includes('shadow')) return 'boxShadow';
  return undefined;
};

const getBreakpointMaskingSlots = (
  component: Component,
  propertyName: string,
): string[] => {
  const style = isRecord(component.style) ? component.style : {};
  return (['mobile', 'tablet', 'desktop'] as const).filter((slot) => {
    const slotStyle = isRecord(style[slot]) ? style[slot] : undefined;
    return slotStyle?.[propertyName] !== undefined;
  });
};

export const ActionCardRenderer: React.FC<ActionCardRendererProps> = ({
  component,
  pages,
  currentPageId,
  updateComponent,
  onAutoSend,
  onPreFill,
  onInjectMessage,
  onDataDiscovery,
  onRemoveData,
  onIconPicker,
  onMapTemplates,
  onColorPicker,
  onDeviceBinding,
  onHomeAssistantBinding,
  onDuplicateComponent,
  onHideComponent,
}) => {
  const props = component.properties;
  const config = getActionCardConfig(component.type);
  const [panelScheme, setPanelScheme] = useState<ColorSchemeWriteTarget>('both');

  useEffect(() => {
    setPanelScheme('both');
  }, [component.id]);

  const warnIfBreakpointMasksVariant = (commandOrPrefix: string) => {
    if (panelScheme === 'both') return;
    const propertyName = stylePropertyFromCommand(commandOrPrefix);
    if (!propertyName) return;

    const maskingSlots = getBreakpointMaskingSlots(component, propertyName);
    if (maskingSlots.length === 0) return;

    onInjectMessage(
      `${panelScheme === 'dark' ? 'Dark' : 'Light'} ${propertyName} edits may be masked on ${maskingSlots.join(', ')} because those breakpoint style slots already define ${propertyName}.`,
    );
  };

  if (!config) return null;

  const renderPinActionCard = (pinContext: PinContext) => {
    const homeAssistantSummary = describeHomeAssistantBinding(props);
    const hasHomeAssistantBinding = homeAssistantSummary !== null;
    const canBindHomeAssistant = supportsHomeAssistantBinding(component.type);

    return (
      <PinActionCard
        pinContext={pinContext}
        component={component}
        pages={pages}
        currentPageId={currentPageId}
        updateComponent={updateComponent}
        onAutoSend={onAutoSend}
        onPreFill={onPreFill}
        onInjectMessage={onInjectMessage}
        onDataDiscovery={onDataDiscovery}
        mapHomeAssistantSummary={homeAssistantSummary}
        mapHomeAssistantButtonLabel={homeAssistantSummary ? 'Change Device' : 'Device'}
        onDeviceBinding={onDeviceBinding}
        onHomeAssistantBinding={canBindHomeAssistant ? onHomeAssistantBinding : undefined}
        onHomeAssistantUnbind={canBindHomeAssistant && hasHomeAssistantBinding
          ? () => {
              updateComponent(component.id, createHomeAssistantUnbindUpdate(component));
              onInjectMessage(`Removed Home Assistant binding metadata from "${component.name || component.type}".`);
            }
          : undefined}
        onHomeAssistantExplain={canBindHomeAssistant && hasHomeAssistantBinding
          ? () => onInjectMessage(createHomeAssistantExplainMessage(component))
          : undefined}
      />
    );
  };

  // Gauge cards
  if (config.category === 'gauge') {
    const homeAssistantSummary = describeHomeAssistantBinding(props);
    const hasHomeAssistantBinding = homeAssistantSummary !== null;
    const gaugeContext: GaugeContext = {
      componentId: component.id,
      title: readString(props.title) || readString(props.name) || 'Gauge',
      gaugeType: component.type === ComponentType.VERTICAL_GAUGE_CHART ? 'Vertical Gauge' : 'Radial Gauge',
      displayMode: readGaugeDisplayMode(props.displayMode),
      value: readNumber(props.value, 0),
      min: readNumber(props.min, 0),
      max: readNumber(props.max, 100),
      unit: readString(props.unit) || '',
      hasDataSource: isRecord(props.dataSource) &&
        !!props.dataSource.type &&
        props.dataSource.type !== 'static',
      thresholds: readThresholds(props.thresholds),
    };
    return (
      <GaugeActionCard
        gaugeContext={gaugeContext}
        homeAssistantSummary={homeAssistantSummary}
        homeAssistantButtonLabel={homeAssistantSummary ? 'Change Device' : 'Device'}
        onAutoSend={onAutoSend}
        onPreFill={onPreFill}
        onDataDiscovery={onDataDiscovery}
        onDeviceBinding={onDeviceBinding}
        onHomeAssistantBinding={onHomeAssistantBinding}
        onHomeAssistantUnbind={hasHomeAssistantBinding
          ? () => {
              updateComponent(component.id, createHomeAssistantUnbindUpdate(component));
              onInjectMessage(`Removed Home Assistant binding metadata from "${component.name || component.type}".`);
            }
          : undefined}
        onHomeAssistantExplain={hasHomeAssistantBinding
          ? () => onInjectMessage(createHomeAssistantExplainMessage(component))
          : undefined}
        onDuplicateComponent={onDuplicateComponent}
        onHideComponent={onHideComponent}
        hideComponentLabel={component.hidden ? 'Show' : 'Hide'}
        onUpdateGauge={({ min, max, thresholds }) => {
          updateComponent(component.id, {
            properties: {
              min,
              max,
              thresholds,
            },
          });
          onInjectMessage(
            `Updated gauge range to ${min}-${max} with ${thresholds.length} segment${thresholds.length === 1 ? '' : 's'}.`
          );
        }}
      />
    );
  }

  // Pin cards (map with selected pin)
  if (config.category === 'pin') {
    const selectedPinId = readString(props.selectedPinId);
    const pins = readPins(props.pins);
    const groups = readGroups(props.groups);
    const pin = selectedPinId ? pins.find(p => p.id === selectedPinId) : null;

    if (pin) {
      const pinContext: PinContext = { pin, groups };
      return renderPinActionCard(pinContext);
    }

    // Map without pin selected — fall through to simple card
  }

  // Map with selected pin — render pin card even though category is 'simple'
  if (component.type === ComponentType.MAP_CHART) {
    const selectedPinId = readString(props.selectedPinId);
    if (selectedPinId) {
      const pins = readPins(props.pins);
      const groups = readGroups(props.groups);
      const pin = pins.find(p => p.id === selectedPinId);
      if (pin) {
        const pinContext: PinContext = { pin, groups };
        return renderPinActionCard(pinContext);
      }
    }
  }

  // Simple cards (including map without pin)
  const title = config.titleFn(props);
  const homeAssistantSummary = describeHomeAssistantBinding(props);
  const hasHomeAssistantBinding = homeAssistantSummary !== null;
  const summary = homeAssistantSummary ?? config.summaryFn?.(props) ?? null;

  // Check if the component has an active data source
  const dataSource = isRecord(props.dataSource) ? props.dataSource : null;
  const hasDataSource = homeAssistantSummary !== null ||
    !!(dataSource?.type && dataSource.type !== 'static') ||
    !!(props.queryTemplateId) ||
    !!(props.valueDataSource);
  const baseButtons = normalizeDeviceBindingButtons(config.buttons, component, hasHomeAssistantBinding);
  const homeAssistantButtons = getHomeAssistantBindingButtons(
    component,
    baseButtons,
    hasHomeAssistantBinding,
  );
  const canBindHomeAssistant = supportsHomeAssistantBinding(component.type);
  const buttons = appendUniqueButtons(
    appendUniqueButtons(
      appendUniqueButtons(baseButtons, homeAssistantButtons),
      getQuickCustomizationButtons(component, baseButtons),
    ),
    getSafeComponentButtons(component, { onDuplicateComponent, onHideComponent }),
  );

  return (
    <ActionCardShell
      icon={config.icon}
      title={title}
      summary={summary}
      buttons={buttons}
      hasDataSource={hasDataSource}
      onAutoSend={onAutoSend}
      onPreFill={onPreFill}
      onDataDiscovery={onDataDiscovery}
      onRemoveData={onRemoveData}
      componentType={component.type}
      onIconPicker={onIconPicker}
      onMapTemplates={onMapTemplates}
      onColorPicker={onColorPicker}
      onDeviceBinding={onDeviceBinding}
      onHomeAssistantBinding={canBindHomeAssistant ? onHomeAssistantBinding : undefined}
      onHomeAssistantUnbind={() => {
        updateComponent(component.id, createHomeAssistantUnbindUpdate(component));
        onInjectMessage(`Removed Home Assistant binding metadata from "${component.name || component.type}".`);
      }}
      onHomeAssistantExplain={() => onInjectMessage(createHomeAssistantExplainMessage(component))}
      onDuplicateComponent={onDuplicateComponent}
      onHideComponent={onHideComponent}
      styleWriteTarget={panelScheme}
      onStyleWriteTargetChange={setPanelScheme}
      onVariantStyleEdit={warnIfBreakpointMasksVariant}
      component={component}
      updateComponent={updateComponent}
      onInjectMessage={onInjectMessage}
    />
  );
};
